import { useEffect, useRef, useState } from "react";
import { Search, UserSearch, Loader2, Phone, Mail } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import api from "../lib/api";
import { formatPhone } from "../lib/utils-format";

const dirName = (r) => [r.first_name, r.last_name].filter(Boolean).join(" ") || r.company_name || "Unnamed";

// Searchable Directory picker. Selecting a contact hands the full entry back so
// the caller can populate Agent Name / Phone / Email on the record.
export function AgentDirectoryPicker({ onSelect, busy }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/directory?limit=12${query ? `&search=${encodeURIComponent(query)}` : ""}`);
        setResults(data.items || []);
      } catch { setResults([]); } finally { setLoading(false); }
    }, 250);
    return () => timer.current && clearTimeout(timer.current);
  }, [query, open]);

  const pick = (contact) => {
    onSelect(contact);
    setOpen(false);
    setQuery("");
  };

  return (
    <div className="lg:col-span-2 py-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" size="sm" disabled={busy} className="h-8" data-testid="agent-directory-picker-btn">
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <UserSearch className="h-3.5 w-3.5 mr-1.5" />}
            Pull agent from Directory
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[340px] p-0" align="start">
          <div className="flex items-center border-b border-border px-3">
            <Search className="h-4 w-4 shrink-0 opacity-50" />
            <Input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, company, email…" className="border-0 focus-visible:ring-0 h-10 px-2" data-testid="agent-directory-search" />
          </div>
          <div className="max-h-72 overflow-y-auto slim-scroll py-1">
            {loading && <div className="px-3 py-2 text-sm text-muted-foreground">Searching…</div>}
            {!loading && results.length === 0 && <div className="px-3 py-2 text-sm text-muted-foreground">No directory entries found.</div>}
            {!loading && results.map((r) => (
              <button key={r.id} type="button" onClick={() => pick(r)} className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left hover:bg-accent transition-colors" data-testid={`agent-directory-option-${r.id}`}>
                <div className="flex items-center gap-2 w-full">
                  <span className="font-medium text-sm truncate">{dirName(r)}</span>
                  {r.type && <span className="text-[10px] uppercase tracking-wide text-muted-foreground border border-border rounded-full px-1.5 py-0.5 shrink-0">{r.type}</span>}
                </div>
                {r.company_name && dirName(r) !== r.company_name && <span className="text-xs text-muted-foreground truncate">{r.company_name}</span>}
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  {(r.mobile_phone || r.business_phone) && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{formatPhone(r.mobile_phone || r.business_phone)}</span>}
                  {r.email && <span className="inline-flex items-center gap-1 truncate"><Mail className="h-3 w-3" />{r.email}</span>}
                </div>
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
